import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import {
  Analysis,
  AnalysisPlanning,
  AnalysisPolygon,
  FileOverviewItem,
  PlanningChartData,
} from '@shared/models/analysis.model';
import { AnalysisViewer, ViewerSelection } from '@shared/components/analysis-viewer/analysis-viewer';
import { AnalysisPlannings } from '@features/analyses/components/analysis-plannings/analysis-plannings';
import { StatePanel } from '@shared/components/state-panel/state-panel';

/**
 * Root layout for "visualization + planning results": the roof visualization
 * with drawn modules next to the planning summary/configuration/charts.
 *
 * Single source of truth for this layout — used by the analysis detail
 * "Planung" tab and the campaign "Planungsergebnisse" overview tab so both
 * stay visually and structurally identical.
 */
@Component({
  selector: 'app-analysis-planning-results-view',
  standalone: true,
  imports: [
    AnalysisViewer,
    AnalysisPlannings,
    StatePanel,
    ...BrnDialogImports,
    ...HlmDialogImports,
  ],
  templateUrl: './analysis-planning-results-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisPlanningResultsView {
  readonly planningViewerSelection: ViewerSelection = { view: 'image', image: 'planung', model: 'with-pv' };
  readonly viewerDialogState = signal<'open' | 'closed'>('closed');

  analysis = input<Analysis | null>(null);
  polygons = input<AnalysisPolygon[] | null>(null);
  filesOverview = input<FileOverviewItem[]>([]);
  plannings = input<AnalysisPlanning[]>([]);
  selectedPlanningId = input<string | null>(null);
  primaryPlanningId = input<string | null>(null);
  isLoadingPlannings = input<boolean>(false);
  mode = input<'admin' | 'public'>('admin');
  canCreatePlanning = input(false);
  isVisible = input<boolean>(true);
  chartDataProvider = input<
    ((planningId: string) => Promise<PlanningChartData | null>) | null
  >(null);
  selectedPlanningIsCompleted = computed(() => {
    const list = this.plannings();
    const selected =
      list.find((planning) => planning.id === this.selectedPlanningId()) ??
      list[0];
    return selected?.status === 'completed';
  });

  selectedPlanningIdChange = output<string>();
  isDeletingPlanning = input(false);
  deletePlanning = output<string>();
  setPrimaryPlanning = output<string>();
  createPlanning = output<void>();
  retryPlanning = output<string>();
  mediaRefreshRequested = output<void>();

  onSelectedPlanningIdChange(planningId: string): void {
    this.selectedPlanningIdChange.emit(planningId);
  }

  onDeletePlanning(planningId: string): void {
    this.deletePlanning.emit(planningId);
  }

  onSetPrimaryPlanning(planningId: string): void {
    this.setPrimaryPlanning.emit(planningId);
  }

  onCreatePlanning(): void {
    this.createPlanning.emit();
  }

  onRetryPlanning(planningId: string): void {
    this.retryPlanning.emit(planningId);
  }

  readonly expandedViewerSelection = signal<ViewerSelection | null>(null);

  openViewerDialog(selection: ViewerSelection): void {
    this.expandedViewerSelection.set(selection);
    this.viewerDialogState.set('open');
  }

  closeViewerDialog(): void {
    this.viewerDialogState.set('closed');
  }
}
